import { useCallback, useEffect, useState } from 'react'

import { ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '~/components/component-library'
import { useNavigation } from '~/hooks/useNavigation'

export const SimulationButton = ({
  runTenderlySimulation,
  defaultSimulationUrl,
  linkTarget = 'tab',
}: {
  runTenderlySimulation?: () => Promise<string | undefined>
  defaultSimulationUrl?: string
  linkTarget?: 'tab' | 'window'
}) => {
  const navigate = useNavigation()
  const [simulationInProgress, setSimulationInProgress] = useState(false)
  const [simulationUrl, setSimulationUrl] = useState(defaultSimulationUrl)

  // keep the state var in sync with the default
  useEffect(() => {
    setSimulationUrl(defaultSimulationUrl)
  }, [defaultSimulationUrl])

  const handleTenderlyClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()

      // don't run a simulation if one already exists
      if (simulationUrl) return navigate(simulationUrl, { target: linkTarget })

      // if no simulation function, throw an error
      if (!runTenderlySimulation) throw new Error('No simulation available')

      // run the simulation
      try {
        setSimulationInProgress(true)
        const url = await runTenderlySimulation()

        if (!url) throw new Error('No simulation URL returned')

        // set a state var in case they click a second time
        setSimulationUrl(url)

        // navigate to the simulation url
        navigate(url, { target: linkTarget })
      } catch (error) {
        toast.error('Simulation failed', {
          description: error.message,
        })
      } finally {
        setSimulationInProgress(false)
      }
    },
    [runTenderlySimulation, simulationUrl, navigate, linkTarget],
  )

  // hide the button if no simulation is available
  if (!runTenderlySimulation && !simulationUrl) return null

  return (
    <Button
      variant="secondaryAction"
      suffix={<ExternalLink strokeWidth={2.5} />}
      isLoading={simulationInProgress}
      onClick={handleTenderlyClick}
      eventName="txn-display:simulate"
      eventProperties={{ simulationUrl }}
    >
      Details
    </Button>
  )
}
